import { useState, useEffect, useCallback, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import apiService from '../../services/ApiService';
import FieldsInitializer, { initTimeSerie } from '../ModelDataFields/Input/Initializer';
import IModelData from '../../types/ModelData';
import { AGENT_DATA_CHANGED_EVENT } from '../../hooks/useAgentChat';
import type { AgentDataChange } from '../../hooks/useAgentChat';

// ── Types ──────────────────────────────────────────────────────

interface TreeData {
    items: Record<string, any>;
    rootId: string;
}

interface OperationalState {
    isComputed: boolean;
    isPersisted: boolean;
    OperationalOutput: { tree: TreeData };
    OperationalInput: { tree: { items: Record<string, any> } };
    OperationalParameters: any;
}

interface ValuationState {
    Output: { tree: TreeData };
}

interface UserRights {
    user_id: number;
    right_to_share: boolean;
    right_to_read: boolean;
    right_to_write: boolean;
    right_to_duplicate: boolean;
}

export interface UseOperationalDataReturn {
    state: OperationalState;
    valuationState: ValuationState;
    loading: boolean;
    conditionComputed: boolean;
    reloadState: () => void;
    reloadFromApi: () => Promise<void>;
    computeValuation: () => Promise<void>;
    userRights: UserRights;
    permanentRightToWrite: boolean;
    userEditing: { user_id: number; user_name: string };
    userClaimingWrite: { user_id: number; user_name: string };
    handleClaimWrite: () => void;
    handleGiveWrite: () => void;
    onDragEndOutput: (result: any) => void;
}

// ── Tree init defaults ─────────────────────────────────────────

const TREE_INIT: TreeData = {
    rootId: '42',
    items: {
        '42': {
            id: '42', name: 'root', type: 'root', position: 0,
            expanded: false, editing: false, anchorEl: null,
            deleteModalOpen: false, childrens: [], possibleChildrens: [], parentId: null,
        },
    },
};

const OPERATIONAL_TREE_INIT = {
    items: {
        '42': {
            id: '42', name: 'root', type: 'root',
            expanded: false, editing: false, anchorEl: null, deleteModalOpen: false,
        },
    },
};

const INITIAL_STATE: OperationalState = {
    isComputed: false,
    isPersisted: false,
    OperationalOutput: { tree: TREE_INIT },
    OperationalInput: { tree: OPERATIONAL_TREE_INIT },
    OperationalParameters: {},
};

const INITIAL_VALUATION: ValuationState = {
    Output: { tree: TREE_INIT },
};

// ── Hook ───────────────────────────────────────────────────────

export default function useOperationalData(
    model: any,
    currentScenario: any,
): UseOperationalDataReturn {
    const [state, setState] = useState<OperationalState>(INITIAL_STATE);
    const [valuationState, setValuationState] = useState<ValuationState>(INITIAL_VALUATION);
    const [loading, setLoading] = useState(true);
    const [conditionComputed, setConditionComputed] = useState(false);
    const [mounted, setMounted] = useState(false);


    // Expanded state tracking (persists across reloads)
    const expandedRef = useRef<Record<string, boolean>>({});

    // ── User rights & collaboration ────────────────────────────

    const [userRights, setUserRights] = useState<UserRights>({
        user_id: 0, right_to_share: false, right_to_read: false,
        right_to_write: false, right_to_duplicate: false,
    });
    const [permanentRightToWrite, setPermanentRightToWrite] = useState(false);
    const [userEditing, setUserEditing] = useState({ user_id: 0, user_name: '' });
    const [userClaimingWrite, setUserClaimingWrite] = useState({ user_id: 0, user_name: '' });

    // ── WebSocket ──────────────────────────────────────────────

    const [socketUrl, setSocketUrl] = useState(
        '/scenario_events_operational/' + currentScenario.id
    );

    // Resolved on every reconnect, so a reconnect never reuses an expired or rotated token.
    const getSocketUrl = useCallback(() => apiService.getWebSocketUrl(socketUrl), [socketUrl]);
    const { sendJsonMessage, lastJsonMessage } = useWebSocket(
        getSocketUrl,
        {
            shouldReconnect: () => true,
            reconnectAttempts: 10,
            reconnectInterval: (attemptNumber: number) =>
                Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
        },
    );

    // Update socket URL on scenario change
    useEffect(() => {
        setSocketUrl('/scenario_events_operational/' + currentScenario.id);
        setUserClaimingWrite({ user_id: 0, user_name: '' });
    }, [currentScenario.id]);

    // Fetch user rights
    useEffect(() => {
        apiService.getUserRightsSharedModels(model.id).then(async (response) => {
            if (response && response.ok) {
                const elem = await response.json();
                setUserRights({
                    user_id: elem.user_id,
                    right_to_share: elem.right_to_share,
                    right_to_read: elem.right_to_read,
                    right_to_write: model.modelRulesDeducted?.can_edit !== false && elem.right_to_write,
                    right_to_duplicate: elem.right_to_duplicate,
                });
                setPermanentRightToWrite(elem.right_to_write);
                sendJsonMessage({ type: 'editable' }, true);
            }
        });
    }, [currentScenario.id]);

    // Handle WebSocket messages
    useEffect(() => {
        if (!lastJsonMessage) return;
        const msg = lastJsonMessage as any;

        if (msg.type === 'editable') {
            if (msg.user_id === userRights.user_id) {
                setUserRights((prev) => ({ ...prev, right_to_write: model.modelRulesDeducted?.can_edit !== false && msg.editable }));
                setUserEditing({ user_id: msg.user_editing_id, user_name: msg.user_editing_name });
                if (msg.user_editing_id === msg.user_id && userEditing.user_name !== '') {
                    loadFromApi();
                }
                reloadState();
            }
        } else if (msg.type === 'request_right') {
            if (msg.user_id === userRights.user_id) {
                setUserClaimingWrite({ user_id: msg.claimer_id, user_name: msg.claimer_name });
                reloadState();
            }
        } else if (msg.type === 'scenario_computed' || msg.type === 'sensitivity_computed') {
            // Reload data to show updated computed values
            loadFromApi();
        }
    }, [lastJsonMessage]);

    const handleClaimWrite = useCallback(() => {
        sendJsonMessage({ type: 'request_right', user_id: userRights.user_id }, true);
    }, [sendJsonMessage, userRights.user_id]);

    const handleGiveWrite = useCallback(() => {
        sendJsonMessage({ type: 'give_right', user_id: userClaimingWrite.user_id }, true);
        setUserClaimingWrite({ user_id: 0, user_name: '' });
    }, [sendJsonMessage, userClaimingWrite.user_id]);

    // ── Data mappers ───────────────────────────────────────────

    const mapModelData = useCallback((modelData: any): any => ({
        id: modelData.id.toString(),
        name: modelData.name,
        type: modelData.type,
        kind: modelData.kind,
        position: modelData.position,
        childrens: modelData.childrens_filtered.map((e: number) => e.toString()),
        possibleChildrens: modelData.possible_childrens,
        childrensDefinition: modelData.childrens_definition,
        parentId: modelData.parent_id ? modelData.parent_id.toString() : null,
        fields: FieldsInitializer(modelData.fields),
        relatedModelDataId: modelData.related_model_id,
        has_related_output: modelData.has_related_output,
        isInconsiderable: modelData.is_inconsiderable,
        isDeletable: modelData.is_deletable,
        expanded: expandedRef.current[modelData.kind + modelData.id.toString()] ?? false,
        anchorEl: null,
        sensitivityModelData: null,
        deleteModalOpen: false,
        detailsModalOpen: false,
    }), []);

    const mapOperationalModelData = useCallback((modelData: any): any => {
        Object.values(modelData.fields).forEach((field: any) => {
            initTimeSerie(field);
        });
        return {
            id: modelData.id.toString(),
            name: modelData.name,
            type: modelData.type,
            modelDataId: modelData.model_data_id,
            fields: modelData.fields,
            anchorEl: null,
            sensitivityModelData: null,
            deleteModalOpen: false,
            detailsModalOpen: false,
        };
    }, []);

    const mapValuationModelData = useCallback((modelData: any): any => ({
        id: modelData.id.toString(),
        name: modelData.name,
        type: modelData.type,
        kind: 'Output',
        childrens: modelData.childrens,
        possibleChildrens: [],
        parentId: modelData.parent_id ? modelData.parent_id.toString() : null,
        fields: FieldsInitializer(modelData.fields),
        expanded: expandedRef.current['Output' + modelData.id.toString()] ?? false,
        anchorEl: null,
        editing: false,
        sensitivityModelData: null,
        deleteModalOpen: false,
        detailsModalOpen: false,
    }), []);

    const mapModelDataList = useCallback((list: any[]) => {
        const result: Record<string, any> = {};
        for (const elem of list) {
            result[elem.id.toString()] = mapModelData(elem);
        }
        return result;
    }, [mapModelData]);

    const mapOperationalModelDataList = useCallback((list: any[]) => {
        const result: Record<string, any> = {};
        for (const elem of list) {
            result[elem.model_data_id.toString()] = mapOperationalModelData(elem);
        }
        return result;
    }, [mapOperationalModelData]);

    const mapValuationModelDataList = useCallback((list: any[]) => {
        const result: Record<string, any> = {};
        for (const elem of list) {
            try {
                result[elem.id.toString()] = mapValuationModelData(elem);
            } catch (e) {
                console.error(e);
            }
        }
        return result;
    }, [mapValuationModelData]);

    const findRootItemId = (list: any[]): string => {
        return list.filter((md: any) => md.parent_id == null)[0].id.toString();
    };

    const mapApiResponse = useCallback((response: any): OperationalState => ({
        isComputed: response.is_computed,
        isPersisted: response.is_persisted,
        OperationalOutput: {
            tree: {
                items: mapModelDataList(response.operational_output),
                rootId: findRootItemId(response.operational_output),
            },
        },
        OperationalInput: {
            tree: {
                items: mapOperationalModelDataList(response.operational_input),
            },
        },
        OperationalParameters: response.compute_parameters,
    }), [mapModelDataList, mapOperationalModelDataList]);

    const mapApiValuationResponse = useCallback((response: any): ValuationState => ({
        Output: {
            tree: {
                items: mapValuationModelDataList(response),
                rootId: findRootItemId(response),
            },
        },
    }), [mapValuationModelDataList]);

    // ── Expanded state persistence ─────────────────────────────

    const setExpandedRef = useCallback(() => {
        const ref = expandedRef.current;
        Object.values(state.OperationalOutput.tree.items).forEach((item: any) => {
            ref['OperationalOutput' + item.id] = item.expanded;
        });
        Object.values(state.OperationalInput.tree.items).forEach((item: any) => {
            ref['OperationalInput' + item.id] = item.expanded;
        });
        Object.values(valuationState.Output.tree.items).forEach((item: any) => {
            ref['Output' + item.id] = item.expanded;
        });
    }, [state, valuationState]);

    // ── API calls ──────────────────────────────────────────────

    const loadFromApi = useCallback(async () => {
        setExpandedRef();
        setLoading(true);
        try {
            const response = await apiService.getOperationalModelDataByScenario(currentScenario.id);
            if (response) {
                const newState = mapApiResponse(await response.json());
                if (!newState.isComputed || !newState.isPersisted) {
                    setConditionComputed(true);
                } else {
                    setState(newState);
                    setConditionComputed(false);
                }
            }
        } catch (error) {
            console.error('Error loading operational data:', error);
        } finally {
            setLoading(false);
        }
    }, [currentScenario.id, mapApiResponse, setExpandedRef]);

    const loadValuationFromApi = useCallback(async () => {
        try {
            const response = await apiService.getValuationModelDataByScenario(currentScenario.id);
            if (response) {
                const newState = mapApiValuationResponse(await response.json());
                setValuationState(newState);
            }
        } catch (error) {
            console.error('Error loading valuation data:', error);
        }
    }, [currentScenario.id, mapApiValuationResponse]);

    // Load on scenario change
    useEffect(() => {
        loadFromApi();
    }, [currentScenario.id]);

    useEffect(() => {
        loadValuationFromApi();
    }, [currentScenario.id]);

    // ── State reload (trigger re-render) ───────────────────────

    const reloadState = useCallback(() => {
        setExpandedRef();
        setMounted((prev) => !prev);
    }, [setExpandedRef]);

    // ── Agent live-update handler ──────────────────────────────
    // Listens for data_changed events dispatched by the AI agent and applies
    // atomic patches to the OME state so that changes are visible immediately
    // without a full reload.  For structural changes (block created/deleted) and
    // complex field reloads we fall back to a full loadFromApi.
    useEffect(() => {
        const handler = (e: Event) => {
            const change = (e as CustomEvent<AgentDataChange>).detail;

            // Only process changes for the current scenario
            if (change.scenario_id !== undefined && change.scenario_id !== currentScenario?.id) return;

            switch (change.type) {
                case 'field_value_updated': {
                    const modelDataId = String(change.model_data_id);
                    const fieldName = change.field_name as string;

                    // Check in OperationalInput (keyed by model_data_id)
                    const inputItem = state.OperationalInput.tree.items[modelDataId];
                    if (inputItem?.fields?.[fieldName]) {
                        const field = inputItem.fields[fieldName];
                        if (change.merge_data && typeof change.merge_data === 'object') {
                            // Time series merge: patch date→value pairs into the .data array
                            const dataArr = field.currentValue?.value?.data ?? field.value?.value?.data;
                            if (Array.isArray(dataArr)) {
                                const mergeEntries = change.merge_data as Record<string, unknown>;
                                for (const [date, val] of Object.entries(mergeEntries)) {
                                    const existing = dataArr.find((el: any) => el.date?.value === date);
                                    if (existing) {
                                        existing.value.value = val;
                                    } else {
                                        dataArr.push({
                                            date: { value: date, address: null },
                                            value: { value: val, address: null },
                                        });
                                    }
                                }
                            }
                        } else {
                            if (field.currentValue) {
                                field.currentValue.value = change.new_value;
                            } else if (field.value) {
                                field.value.value = change.new_value;
                            }
                        }
                        reloadState();
                        break;
                    }

                    // Check in OperationalOutput (keyed by id)
                    const outputItem = state.OperationalOutput.tree.items[modelDataId];
                    if (outputItem?.fields?.[fieldName]) {
                        const field = outputItem.fields[fieldName];
                        if (change.merge_data && typeof change.merge_data === 'object') {
                            const dataArr = field.currentValue?.value?.data ?? field.value?.value?.data;
                            if (Array.isArray(dataArr)) {
                                const mergeEntries = change.merge_data as Record<string, unknown>;
                                for (const [date, val] of Object.entries(mergeEntries)) {
                                    const existing = dataArr.find((el: any) => el.date?.value === date);
                                    if (existing) {
                                        existing.value.value = val;
                                    } else {
                                        dataArr.push({
                                            date: { value: date, address: null },
                                            value: { value: val, address: null },
                                        });
                                    }
                                }
                            }
                        } else {
                            if (field.currentValue) {
                                field.currentValue.value = change.new_value;
                            } else if (field.value) {
                                field.value.value = change.new_value;
                            }
                        }
                        reloadState();
                    }
                    break;
                }

                case 'field_reload':
                case 'sensitivity_field_updated':
                case 'sensitivity_field_reload': {
                    // Full reload from API for complex field changes
                    loadFromApi();
                    break;
                }

                case 'block_created':
                case 'block_deleted': {
                    // Structural change — reload from API
                    loadFromApi();
                    break;
                }
            }
        };

        window.addEventListener(AGENT_DATA_CHANGED_EVENT, handler);
        return () => window.removeEventListener(AGENT_DATA_CHANGED_EVENT, handler);
    }, [currentScenario?.id, state]);

    // ── Compute valuation ──────────────────────────────────────

    const computeValuation = useCallback(async () => {
        try {
            const response = await apiService.computeValuationModelDataByScenario(currentScenario.id);
            if (response) {
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[computeValuation] Backend error:', response.status, errorText);
                    return;
                }
                const newState = mapApiValuationResponse(await response.json());
                setValuationState(newState);
            }
        } catch (error) {
            console.error('Error computing valuation:', error);
        }
    }, [currentScenario.id, mapApiValuationResponse]);

    // ── Drag & drop ────────────────────────────────────────────

    const reorder = (list: any[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEndOutput = useCallback((result: any) => {
        if (!result.destination) return;
        const { tree } = state.OperationalOutput;
        const movedItem = tree.items[result.draggableId];

        apiService.putOutputModelData(movedItem.id, result.destination.index);

        tree.items[movedItem.parentId].childrens = reorder(
            tree.items[movedItem.parentId].childrens,
            result.source.index,
            result.destination.index,
        );
        reloadState();
    }, [state, reloadState]);

    return {
        state,
        valuationState,
        loading,
        conditionComputed,
        reloadState,
        reloadFromApi: loadFromApi,
        computeValuation,
        userRights,
        permanentRightToWrite,
        userEditing,
        userClaimingWrite,
        handleClaimWrite,
        handleGiveWrite,
        onDragEndOutput,
    };
}
